import type { ShallowDehydrateObject } from 'kysely'
import { z } from 'zod'

import type {
  Logger,
  LoggerEventInitializer,
  LoggerHistoryInitializer,
  LoggerHistoryMutator,
  LoggerInitializer,
  LoggerMutator,
  ModemHistoryInitializer,
  ModemId,
  OrganizationId,
  ProjectId,
  UserId,
  LoggerHistoryView,
  LoggerModemsView,
  LoggerEventsView,
  LoggerCurrentModemView,
  LoggerLastTransmissionView,
  Subscriber,
  LoggerCurrentHistoryView,
  LoggerCloudloopView,
  LoggerActivationsView,
} from '../../index.js'
import {
  loggerEvent,
  loggerHistory,
  loggerHistoryMutator,
  loggerInitializer,
  loggerMutator,
  modemHistory,
  organizationId,
  projectId,
} from '../../index.js'

export type LoggerWithContextInitializer = LoggerInitializer &
  (
    | {
        historyAction: 'appendHistory'
        history: Omit<LoggerHistoryInitializer, 'id' | 'loggerId'>
      }
    | {
        historyAction: 'populateHistory'
        history: Omit<LoggerHistoryInitializer, 'id' | 'loggerId'>[]
      }
    | { historyAction?: undefined; history?: undefined }
  ) &
  (
    | {
        eventsAction: 'appendEvents'
        events: Omit<LoggerEventInitializer, 'id' | 'loggerId'>
      }
    | {
        eventsAction: 'populateEvents'
        events: Omit<LoggerEventInitializer, 'id' | 'loggerId'>[]
      }
    | { eventsAction?: undefined; events?: undefined }
  ) &
  (
    | {
        modemAction: 'appendModems'
        modems: Omit<ModemHistoryInitializer, 'loggerId'> & {
          imei: string
          createdBy?: UserId
          createdAt?: Date | string
          modemId: never
        }
      }
    | {
        modemAction: 'populateModems'
        modems: Array<
          Omit<ModemHistoryInitializer, 'loggerId'> & {
            imei: string
            createdBy: UserId
            createdAt: Date | string
            modemId: never
          }
        >
      }
    | {
        modemAction: 'createIfNotExists'
        modems: Array<
          Omit<ModemHistoryInitializer, 'loggerId' | 'modemId'> & {
            imei: string
            modemId?: ModemId
            createdBy?: UserId
            createdAt?: Date | string
          }
        >
      }
    | {
        modemAction?: undefined
        modems?: undefined
      }
  )

const loggerContextInitializerFields = z.object({
  historyAction: z.string().optional(),
  history: z
    .array(
      z.object({
        startedAt: z.coerce.date(),
        endedAt: z.coerce.date().nullable().optional(),
        inventoryStatus: z.string().optional(),
        organizationId: organizationId,
        projectId: projectId,
      }),
    )
    .optional(),
  eventsAction: z.string().optional(),
  events: z
    .array(
      z.object({
        eventType: z.string(),
        eventDate: z.coerce.date(),
        raw: z.string().optional(),
        text: z.string().optional(),
        cableSerial: z.number().nullable().optional(),
        onewireId: z.string().nullable().optional(),
      }),
    )
    .optional(),
  modemAction: z.string().optional(),
  modems: z
    .array(
      z.object({
        modemId: z.number().optional(),
        imei: z.string(),
        startedAt: z.coerce.date(),
        endedAt: z.coerce.date().nullable().optional(),
        notes: z.string().optional(),
      }),
    )
    .optional(),
})

export const loggerWithContextInitializer = z.object({
  ...loggerContextInitializerFields.shape,
  ...(loggerInitializer as unknown as z.ZodObject<any>).shape,
}) as unknown as z.Schema<LoggerWithContextInitializer>

// Embedded via kysely's jsonArrayFrom/jsonObjectFrom: postgres json_agg
// serializes timestamps to ISO strings and node-postgres does not revive them,
// so date columns arrive as strings. ShallowDehydrateObject is the same
// transformation the query builder applies, so these types cannot drift from
// the real row shape.
export type EmbeddedLoggerHistory =
  | ShallowDehydrateObject<LoggerHistoryView>
  | ShallowDehydrateObject<LoggerCurrentHistoryView>

export type EmbeddedLoggerEvent = ShallowDehydrateObject<LoggerEventsView>

export type EmbeddedLoggerModem = ShallowDehydrateObject<LoggerModemsView>

export type EmbeddedCurrentModem =
  ShallowDehydrateObject<LoggerCurrentModemView>

export type EmbeddedLoggerLastTransmission =
  ShallowDehydrateObject<LoggerLastTransmissionView>

export type EmbeddedLoggerActivation =
  ShallowDehydrateObject<LoggerActivationsView>

export type EmbeddedCloudloopCache = Omit<
  LoggerCloudloopView,
  'updatedAt' | 'cacheData'
> & {
  updatedAt: Date | string
  cacheData: { subscriber?: Subscriber }
}

export type LoggerSiteUse = {
  id: number
  siteName: string
  projectId: ProjectId
  organizationId: OrganizationId
  loggerHistory: Array<{
    startedAt: Date | string
    endedAt: Date | string | null
    loggerSerial: string | null
    decode: number[] | null
  }>
}

export type LoggerWithContext = Logger & {
  history: EmbeddedLoggerHistory[]
  events: EmbeddedLoggerEvent[]
  currentModem?: EmbeddedCurrentModem | null
  modems?: EmbeddedLoggerModem[]
  cloudloop: EmbeddedCloudloopCache | null
  lastTransmission?: EmbeddedLoggerLastTransmission | null
  activations?: EmbeddedLoggerActivation[]
  sites?: LoggerSiteUse[]
}

const loggerContextFields = z.object({
  history: z.array(
    z.object({
      loggerSerial: z.string(),
      ...(loggerHistory as unknown as z.ZodObject<any>).shape,
    }),
  ),
  events: z.array(loggerEvent),
  modems: z.array(
    z.object({
      networkStatus: z.string(),
      imei: z.string(),
      platform: z.string(),
      provider: z.string(),
      ...(modemHistory as unknown as z.ZodObject<any>).shape,
    }),
  ),
  cloudloop: z.object({
    updatedAt: z.coerce.date(),
    cacheData: z.unknown(),
    hardwareId: z.string(),
    subscriberId: z.string(),
    networkStatus: z.string(),
  }),
  lastTransmission: z.object({
    battery: z.number().nullable().optional(),
    batteryTrend: z.unknown().nullable().optional(),
    msgSize: z.number().nullable().optional(),
    errors85: z.number().nullable().optional(),
    errors99: z.number().nullable().optional(),
    errors125: z.number().nullable().optional(),
    numConnectedDevices: z.number().nullable().optional(),
    recievedAt: z.coerce.date(),
  }),
})

export const loggerWithContext = z.object({
  ...loggerContextFields.shape,
  ...(loggerInitializer as unknown as z.ZodObject<any>).shape,
}) as unknown as z.Schema<LoggerWithContext>

export type LoggerModel =
  | 'D605'
  | 'SPOT'
  | 'SL'
  | 'D505'
  | 'DLB'
  | 'NGT'
  | 'Omnidot'
  | 'HOBOlink'

export const LoggerModels = [
  'D605',
  'SPOT',
  'SL',
  'D505',
  'DLB',
  'NGT',
  'Omnidot',
  'HOBOlink',
]

export const loggerModels = z.enum([
  'D605',
  'SPOT',
  'SL',
  'D505',
  'DLB',
  'NGT',
  'Omnidot',
  'HOBOlink',
])

export type LoggerWithContextMutator = LoggerMutator & {
  history?: EmbeddedLoggerHistory[] | null
  modems?: EmbeddedLoggerModem[] | null
  events?: EmbeddedLoggerEvent[] | null
  cloudloop?: EmbeddedCloudloopCache | null
  lastTransmission?: EmbeddedLoggerLastTransmission | null
  newModem?: Omit<ModemHistoryInitializer, 'loggerId' | 'modemId'> & {
    imei: string
    modemId?: ModemId
    createdBy?: UserId
    createdAt?: Date | string
  }
}

const loggerContextMutatorFields = z.object({
  newModem: z
    .object({
      modemId: z.number().optional(),
      imei: z.string(),
      startedAt: z.coerce.date().nullable().optional(),
      endedAt: z.coerce.date().nullable().optional(),
      notes: z.string().nullable().optional(),
    })
    .optional(),
})

export const loggerWithContextMutator = z.object({
  ...loggerContextMutatorFields.shape,
  ...(loggerMutator as unknown as z.ZodObject<any>).shape,
}) as unknown as z.Schema<LoggerWithContextMutator>

export const bulkLoggerHistoryMutator = z.array(
  loggerHistoryMutator,
) as unknown as z.Schema<LoggerHistoryMutator[]>
